// Usage tiers shared by the portal, the backend and the docs. Amounts are in minor units of the
// billing currency (pence). Kept free of zod so any page can import it.

/**
 * PLACEHOLDER AMOUNTS: adjust before launch. Each tier sets the most an organisation can spend in a
 * billing period (`capMinor`, before prepaid credit) and the unpaid usage at which Stripe invoices
 * and charges it straight away (`thresholdMinor`). An organisation reaches a tier once its paid
 * invoices meet `requires`; tier 0 needs only a card on file.
 */
export const TRUST_TIERS = [
  { tier: 0, capMinor: 5_000, thresholdMinor: 2_500, requires: { paidInvoices: 0, paidMinor: 0 } },
  {
    tier: 1,
    capMinor: 25_000,
    thresholdMinor: 10_000,
    requires: { paidInvoices: 1, paidMinor: 0 },
  },
  {
    tier: 2,
    capMinor: 100_000,
    thresholdMinor: 25_000,
    requires: { paidInvoices: 3, paidMinor: 10_000 },
  },
  {
    tier: 3,
    capMinor: 500_000,
    thresholdMinor: 50_000,
    requires: { paidInvoices: 6, paidMinor: 100_000 },
  },
] as const;

export type TrustTierNumber = (typeof TRUST_TIERS)[number]["tier"];
/** A tier from payment history, or `custom` limits set in the back office. */
export type TrustTierId = TrustTierNumber | "custom";

/** Payments that count towards promotion: paid invoices, net of refunds and disputes. */
export interface PaymentHistory {
  paidInvoices: number;
  paidMinor: number;
}

/** The highest tier the payment history meets. */
export const getTierForPaymentHistory = (history: PaymentHistory): TrustTierNumber =>
  [...TRUST_TIERS]
    .reverse()
    .find(
      (tier) =>
        history.paidInvoices >= tier.requires.paidInvoices &&
        history.paidMinor >= tier.requires.paidMinor,
    )?.tier ?? 0;

export const getTrustTier = (tier: TrustTierNumber) =>
  TRUST_TIERS.find((candidate) => candidate.tier === tier) ?? TRUST_TIERS[0];

/** The tier after `tier`, or null at the top. */
export const getNextTrustTier = (tier: TrustTierId) =>
  tier === "custom" ? null : (TRUST_TIERS.find((candidate) => candidate.tier === tier + 1) ?? null);

export interface TrustOverrideLimits {
  tier: TrustTierId;
  capMinor?: number | null;
  thresholdMinor?: number | null;
}

export interface TrustLimits {
  tier: TrustTierId;
  capMinor: number;
  thresholdMinor: number;
  /** `override` while back-office limits apply, else `history`. */
  source: "history" | "override";
}

/**
 * The limits in force: a back-office override when set, else the tier from payment history, held
 * at tier 0 while a dispute is unresolved.
 */
export const resolveTrustLimits = (state: {
  history: PaymentHistory;
  held?: boolean;
  override?: TrustOverrideLimits | null;
}): TrustLimits => {
  const override = state.override;
  if (override) {
    const base = getTrustTier(override.tier === "custom" ? 0 : override.tier);
    return {
      tier: override.tier,
      capMinor: override.capMinor ?? base.capMinor,
      thresholdMinor: override.thresholdMinor ?? base.thresholdMinor,
      source: "override",
    };
  }
  const tier = getTrustTier(state.held ? 0 : getTierForPaymentHistory(state.history));
  return {
    tier: tier.tier,
    capMinor: tier.capMinor,
    thresholdMinor: tier.thresholdMinor,
    source: "history",
  };
};

/** Returned (409) when a blocking spend limit above the usage cap is saved. */
export const SPEND_LIMIT_ABOVE_CAP_ERROR_CODE = "payment/spend-limit-above-cap";
